import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Wordmark } from '@/components/Wordmark';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { apiFetch, ApiError } from '@/lib/api';

interface SignInResponse {
  user?: { id: string; email: string };
  token?: string;
  error?: string;
}

/**
 * Whitelist `?return=…` to relative same-origin paths so we can't be used
 * as an open redirect to phishing sites. Anything else falls back to /app.
 */
function safeReturnTo(raw: string | null, fallback = '/app'): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

export function SignInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('return'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch<SignInResponse>('/api/auth/sign-in/email', {
        method: 'POST',
        json: { email, password },
      });
      navigate(returnTo, { replace: true });
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.payload === 'object' && e.payload && 'message' in e.payload
          ? String((e.payload as { message: string }).message)
          : e instanceof Error
            ? e.message
            : 'Sign in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function continueWithGoogle() {
    setError(null);
    setSubmitting(true);
    try {
      const data = await apiFetch<{ url?: string; redirect?: boolean }>(
        '/api/auth/sign-in/social',
        { method: 'POST', json: { provider: 'google', callbackURL: returnTo } },
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Google sign-in did not return a redirect URL.');
      }
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.payload === 'object' && e.payload && 'message' in e.payload
          ? String((e.payload as { message: string }).message)
          : e instanceof Error
            ? e.message
            : 'Google sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold tracking-tight">Sign in to SendFast</h1>
      <p className="text-muted-foreground mt-1 text-sm">Welcome back.</p>

      <Button
        type="button"
        variant="outline"
        className="mt-8 w-full shadow-xs"
        onClick={continueWithGoogle}
        disabled={submitting}
      >
        <GoogleIcon />
        Continue with Google
      </Button>

      <Divider label="or sign in with email" />

      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Email">
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="text-muted-foreground mt-6 text-center text-sm">
        New to SendFast?{' '}
        <Link
          to={`/sign-up${returnTo === '/app' ? '' : `?return=${encodeURIComponent(returnTo)}`}`}
          className="text-primary hover:underline"
        >
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center px-6 pt-16 pb-12">
      <Wordmark to="/" className="mb-10" />
      <Card className="w-full max-w-md border shadow-sm">
        <CardContent className="p-8">{children}</CardContent>
      </Card>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div>{children}</div>
    </div>
  );
}

export function Divider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3">
      <Separator className="flex-1" />
      <span className="text-muted-foreground shrink-0 text-xs uppercase tracking-wider">{label}</span>
      <Separator className="flex-1" />
    </div>
  );
}

export function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.78 2.73v2.27h2.88c1.69-1.55 2.66-3.84 2.66-6.64z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.88-2.27c-.8.54-1.83.86-3.08.86-2.36 0-4.36-1.6-5.07-3.74H.96v2.34A8.99 8.99 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.93 10.67A5.42 5.42 0 0 1 3.64 9c0-.58.1-1.14.29-1.67V4.99H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.83.96 4.01l2.97-2.34z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.32 0 2.51.45 3.44 1.34l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.99l2.97 2.34C4.64 5.18 6.64 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
